import { Box, Button, Icon, Image, Tooltip } from "@nubeteck/components";
import { Dropdown, MenuProps, Radio, Skeleton } from "antd";
import React, { useEffect, useState } from "react";
import { SubjectSVG } from "src/assets";
import { ServiceSVG } from "src/assets";
import {
  ActivePriceStyled,
  DropdownIcon,
  InactivePriceStyled,
  MoreDetailsButtonStyled,
  ResumeCardDuesCheckContainer,
  ResumeCardIconContainer,
  ResumeCardStyled,
  ResumeFlexStyled,
} from "./resumeCard.styled";
import { ICartItem } from "@/Interfaces/cart";
import { FormatterUtils } from "@nubeteck/utils";
import { useLocale } from "../../../i18n/localeContext";
import { getUserEnrollementPage } from "../../../Api/content";
import { StrapiError } from "../../../pages/StrapiError";

interface IResumeCard extends ICartItem {
  title: string;
  codigo: string;
  type: "subject" | "service" | "balance";
  items: (data: any) => MenuProps["items"];
  isLoading?: boolean;
  fechaVencimiento?: string;
  marcarParaCuotas?: (matriculaId: number, costo: number) => void;
  pagaCuotas?: boolean;
}

const ResumeCard = ({
  type,
  title,
  sePuedePagarEnCuotas,
  marcarParaCuotas,
  codigo,
  horarios,
  costoOriginal,
  iva,
  asignaturaId,
  costoConDescuentos,
  descuentoBeca,
  descuentoPatrocinio,
  items,
  esObligatorio,
  matriculaId,
  fechaVencimiento,
  costo,
  monedaId,
  seccionId,
  pagaCuotas,
  solicitudId,
  reglaTipoId,
}: IResumeCard) => {
  const [isCardExpanded, setCardExpanded] = React.useState<boolean>(false);
  const [itemsArray, setItemsArray] = React.useState<any>([]);
  const { locale } = useLocale();
  const [userEnrollementPage, setUserEnrollementPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setUserEnrollementPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getUserEnrollementPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setUserEnrollementPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  React.useEffect(() => {
    setItemsArray(
      items({
        codigo,
        nombre: title,
        horarios,
        costoOriginal,
        type,
        solicitudId,
        asignaturaId,
        esObligatorio,
        matriculaId,
        seccionId,
      }),
    );
  }, [
    items,
    setItemsArray,
    asignaturaId,
    codigo,
    title,
    horarios,
    costoOriginal,
    type,
    esObligatorio,
    matriculaId,
    seccionId,
    solicitudId,
  ]);

  useEffect(() => {
    if (marcarParaCuotas && reglaTipoId === 2 && !pagaCuotas) {
      marcarParaCuotas(matriculaId, costoConDescuentos ?? costo ?? costoOriginal ?? 0);
    }
  }, [
    marcarParaCuotas,
    reglaTipoId,
    matriculaId,
    costoConDescuentos,
    costo,
    costoOriginal,
    pagaCuotas,
  ]);

  const getSesion = () => {
    return monedaId === 2 ? "₡" : "$";
  };

  const getPaymentOptions = () => {
    if (reglaTipoId === 2) {
      return [{ label: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.pay_installments || "Pagar en cuotas"), value: true }];
    } else {
      return [
        { label: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.pay_all || "Pagar al contado"), value: false },
        { label: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.pay_installments || "Pagar en cuotas"), value: true },
      ];
    }
  };

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setUserEnrollementPage(null);
      }}
    />;
  }
  return (
    <ResumeCardStyled
      className={isCardExpanded ? (type === "service" ? "is-service-expanded" : "is-expanded") : ""}
    >
      <ResumeFlexStyled>
        <Box className={`img-container ${isCardExpanded ? "is-expanded" : ""}`}>
          <Image preview={false} src={type === "subject" ? SubjectSVG : ServiceSVG} width={115} />
        </Box>
        <Box style={{ maxWidth: 255 }}>
          <h4>{title}</h4>
          {type !== "balance" ? (
            <span>
              <strong>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.code_detail || "Código")}: </strong>
              {codigo}
            </span>
          ) : (
            <span>
              <strong>Fecha vencimiento: </strong>
              {fechaVencimiento}
            </span>
          )}
          {isCardExpanded && (
            <Box>
              {horarios != null && (
                <>
                  <h5>
                    <strong>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.schedule_detail || "Horario")}:</strong>
                  </h5>
                  <ul className="horarios">
                    {horarios?.map((hor) => (
                      <li
                        key={`${hor.diaId} ${hor.horaInicio} ${hor.horaFin}`}
                      >{`${hor.diaNombre}, ${hor.horaInicio} - ${hor.horaFin}`}</li>
                    ))}
                  </ul>
                  <span>
                    <strong>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.original_price || "Precio original")}: </strong>
                    {`${getSesion()} ${
                      costoOriginal && costoOriginal > 0
                        ? FormatterUtils.number(costoOriginal.toFixed(2))
                        : 0
                    }`}
                  </span>
                  <div>
                    <strong>IVA: </strong>
                    {`${getSesion()} ${iva && iva > 0 ? FormatterUtils.number(iva.toFixed(2)) : 0}`}
                  </div>
                </>
              )}

              <ul className="descuentos">
                <li>
                  <strong>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.scholarship || "(-) Beca ")}</strong>
                  {`${getSesion()} ${
                    descuentoBeca > 0 ? FormatterUtils.number(descuentoBeca.toFixed(2)) : 0
                  }`}
                </li>

                <li>
                  <strong>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.sponsorship || "(-) Patrocinio ")}</strong>
                  {`${getSesion()} ${
                    descuentoPatrocinio > 0
                      ? FormatterUtils.number(descuentoPatrocinio.toFixed(2))
                      : 0
                  }`}
                </li>
              </ul>
            </Box>
          )}
          {type !== "balance" && (
            <Box>
              <MoreDetailsButtonStyled
                type="link"
                onClick={() => {
                  setCardExpanded(!isCardExpanded);
                }}
              >
                {isCardExpanded ? (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.close_detail || "Ocultar detalles")) : (isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.show_deatil || "Ver más"))}
              </MoreDetailsButtonStyled>
            </Box>
          )}
          {descuentoBeca > 0 || descuentoPatrocinio > 0 ? (
            <>
              <ActivePriceStyled>{`${getSesion()} ${
                costoConDescuentos && costoConDescuentos > 0
                  ? FormatterUtils.number(costoConDescuentos.toFixed(2))
                  : 0
              }`}</ActivePriceStyled>{" "}
              {isCardExpanded && (
                <InactivePriceStyled>
                  {`${getSesion()} ${
                    (costoOriginal && costoOriginal > 0) || (costo && costo > 0)
                      ? FormatterUtils.number((costo ?? costoOriginal ?? 0).toFixed(2))
                      : 0
                  }`}
                </InactivePriceStyled>
              )}
            </>
          ) : (
            <div>
              <ActivePriceStyled>{`${getSesion()} ${
                (costoOriginal && costoOriginal > 0) || (costo && costo > 0)
                  ? FormatterUtils.number((costo ?? costoOriginal ?? 0).toFixed(2))
                  : 0
              }`}</ActivePriceStyled>
            </div>
          )}
        </Box>
      </ResumeFlexStyled>
      {itemsArray &&
      itemsArray?.length > 0 &&
      (type === "subject" || (type === "service" && !esObligatorio)) ? (
        itemsArray.length > 1 ? (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: items({
                codigo,
                nombre: title,
                horarios,
                costoOriginal,
                type,
                asignaturaId,
                esObligatorio,
                seccionId,
              }),
            }}
          >
            <ResumeCardIconContainer>
              <DropdownIcon name="" />
            </ResumeCardIconContainer>
          </Dropdown>
        ) : (
          <ResumeCardIconContainer>
            <Button
              type="text"
              icon={itemsArray[0]?.icon}
              style={{ padding: 0 }}
              onClick={itemsArray[0]?.onClick}
            ></Button>
          </ResumeCardIconContainer>
        )
      ) : null}
      {sePuedePagarEnCuotas && marcarParaCuotas && pagaCuotas !== undefined ? (
        <ResumeCardDuesCheckContainer>
          <Radio.Group
            value={reglaTipoId === 2 ? true : pagaCuotas}
            options={getPaymentOptions()}
            onChange={() =>
              marcarParaCuotas(matriculaId, costoConDescuentos ?? costo ?? costoOriginal ?? 0)
            }
          />
          <Tooltip title={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.cart_selection.pay_message || "Esta materia se dividirá en tres pagos (cuotas).")}>
            <Button type="text" size="small">
              <Icon name="" size={20} colorPrimary />
            </Button>
          </Tooltip>
        </ResumeCardDuesCheckContainer>
      ) : null}
    </ResumeCardStyled>
  );
};

export default ResumeCard;
